import React, { useState, useEffect } from 'react';
import Header from '../components/landing/Header';
import TopBar from '../components/landing/TopBar';
import Footer from '../components/landing/Footer';
import EnquiryModal from '../components/landing/EnquiryModal';
import { Phone, Mail, MapPin, Send } from 'lucide-react';
import api from '../utils/axios';

const Contact = () => {
    const [isScrolled, setIsScrolled] = useState(false);
    const [isInquiryOpen, setIsInquiryOpen] = useState(false);
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        phone: '',
        college: '',
        subject: '',
        message: ''
    });
    const [activeColleges, setActiveColleges] = useState([]);
    const [allPrograms, setAllPrograms] = useState([]);
    const [filteredColleges, setFilteredColleges] = useState([]);
    const [content, setContent] = useState({});
    const [formStatus, setFormStatus] = useState(null);

    useEffect(() => {
        window.scrollTo(0, 0);
        const handleScroll = () => setIsScrolled(window.scrollY > 50);
        window.addEventListener('scroll', handleScroll);
        fetchContent();
        fetchColleges();
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    const fetchColleges = async () => {
        try {
            const res = await api.get('/colleges/active');
            const data = res.data.data;
            setActiveColleges(data);

            // Build unique programs list across all colleges
            const programs = Array.from(new Set(data.flatMap(c => c.programs || [])));
            setAllPrograms(programs);

            if (programs.length > 0) {
                const firstProg = programs[0];
                const matching = data.filter(c => c.programs && c.programs.includes(firstProg));
                setFilteredColleges(matching.length > 0 ? matching : data);
                setFormData(prev => ({
                    ...prev,
                    subject: firstProg,
                    college: matching.length > 0 ? matching[0].name : (data[0]?.name || '')
                }));
            } else {
                setFilteredColleges(data);
                if (data.length > 0) {
                    setFormData(prev => ({ ...prev, college: data[0].name }));
                }
            }
        } catch (err) {
            console.error('Failed to fetch colleges', err);
        }
    };

    // Program selected first → filter institutions
    const handleProgramChange = (e) => {
        const prog = e.target.value;
        const matching = activeColleges.filter(c => c.programs && c.programs.includes(prog));
        const display = matching.length > 0 ? matching : activeColleges;
        setFilteredColleges(display);
        setFormData(prev => ({
            ...prev,
            subject: prog,
            college: display.length > 0 ? display[0].name : ''
        }));
    };

    const handleCollegeChange = (e) => {
        setFormData(prev => ({ ...prev, college: e.target.value }));
    };

    const fetchContent = async () => {
        try {
            const res = await api.get('/content');
            const contentMap = {};
            res.data.data.forEach(item => {
                contentMap[item.key] = item.value;
            });
            setContent(contentMap);
        } catch (err) {
            console.error("Failed to load web content", err);
        }
    };

    /*
    const collegesList = (() => {
        try {
            return content.colleges_list ? JSON.parse(content.colleges_list) : [];
        } catch(e) {
            return [];
        }
    })();
    */

    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setFormStatus('sending');
        try {
            // Map subject to program for the backend model
            const submissionData = {
                ...formData,
                program: formData.subject,
                college: formData.college,
                leadType: 'Message'
            };
            await api.post('/leads/submit', submissionData);
            setFormStatus('success');
            setFormData({
                name: '',
                email: '',
                phone: '',
                college: activeColleges[0]?.name || '',
                subject: activeColleges[0]?.programs?.[0] || activeColleges[0]?.name || 'General Inquiry',
                message: ''
            });
            setTimeout(() => setFormStatus(null), 5000);
        } catch (err) {
            console.error("Form submission failed", err);
            setFormStatus('error');
        }
    };

    return (
        <div className="min-h-screen bg-slate-50 font-sans text-slate-900">
            {!isScrolled && <TopBar data={content} />}
            <Header 
                isScrolled={isScrolled} 
                onApply={() => setIsInquiryOpen(true)}
                logo={content.site_logo}
                email={content.contact_email}
                phone={content.contact_phone}
                applyUrl={content.apply_url}
                colleges={content.colleges_list}
                placements={content.placements_list}
                research={content.research_list}
                journals={content.journals_list}
                innovations={content.innovations_list}
            />

            {/* Hero Section */}
            <header className="relative h-[300px] bg-slate-900 flex items-center overflow-hidden">
                <div className="absolute inset-0 bg-[#003366]/90 z-10"></div>
                <div className="max-w-7xl mx-auto px-4 relative z-20 w-full">
                    <h1 className="text-5xl md:text-6xl font-black text-white uppercase tracking-tighter">
                        Contact <span className="text-[#cc0000]">Us</span>
                    </h1>
                    <p className="text-slate-400 font-bold uppercase tracking-[4px] text-xs mt-4">We are here to help you</p>
                </div>
            </header>

            {/* Contact Grid */}
            <section className="py-24">
                <div className="max-w-7xl mx-auto px-4">
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
                        
                        {/* 1. Direct Contact Info */}
                        <div className="lg:col-span-1 space-y-8">
                            <div className="bg-white p-10 rounded-sm shadow-xl border-t-4 border-[#cc0000]">
                                <h3 className="text-2xl font-black uppercase text-slate-900 mb-8 border-b-2 border-slate-100 pb-4">Head Office</h3>
                                <div className="space-y-6">
                                    <div className="flex items-start space-x-4">
                                        <div className="bg-slate-100 p-3 rounded-sm text-[#cc0000] shrink-0">
                                            <Phone size={20} />
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <p className="text-[10px] font-black uppercase text-slate-400">Call Us</p>
                                            <div className="flex flex-col gap-0.5">
                                                {(content.contact_phone || '+91 91234 56789')
                                                    .split(',')
                                                    .map((num, i) => (
                                                        <p key={i} className="font-bold text-lg whitespace-nowrap">{num.trim()}</p>
                                                    ))
                                                }
                                            </div>
                                        </div>
                                    </div>
                                    <div className="flex items-start space-x-4">
                                        <div className="bg-slate-100 p-3 rounded-sm text-[#cc0000] shrink-0">
                                            <Mail size={20} />
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <p className="text-[10px] font-black uppercase text-slate-400">Email Address</p>
                                            <p className="font-bold text-base break-all leading-snug">{content.contact_email || 'info@aryasabhyata.edu.in'}</p>
                                        </div>
                                    </div>
                                    <div className="flex items-start space-x-4">
                                        <div className="bg-slate-100 p-3 rounded-sm text-[#cc0000] shrink-0">
                                            <MapPin size={20} />
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <p className="text-[10px] font-black uppercase text-slate-400">Campus Address</p>
                                            <p className="font-bold text-slate-600 leading-relaxed uppercase text-sm break-words">
                                                {content.contact_address || "Surendralal Das Teachers' Training College, Anandanagar, Nischinda, Bally, Howrah, West Bengal 711227"}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="bg-[#003366] p-10 rounded-sm text-white">
                                <h4 className="text-lg font-black uppercase mb-4 tracking-widest text-amber-400">Admissions Open</h4>
                                <p className="text-sm font-medium text-slate-300 mb-6">Connect with our learning consultants for academic guidance and program details.</p>
                                <button onClick={() => setIsInquiryOpen(true)} className="w-full bg-[#cc0000] py-4 font-black uppercase text-xs tracking-[4px] hover:bg-white hover:text-[#cc0000] transition-all">
                                    Connect Now
                                </button>
                            </div>
                        </div>

                        {/* 2. Contact Form */}
                        <div className="lg:col-span-2">
                            <div className="bg-white p-12 rounded-sm shadow-xl h-full">
                                <h3 className="text-3xl font-black uppercase text-slate-900 mb-2">Send us a <span className="text-[#cc0000]">message</span></h3>
                                <p className="text-slate-400 font-bold uppercase text-[10px] tracking-widest mb-10">Required fields are marked with *</p>
                                
                                <form onSubmit={handleSubmit} className="space-y-8">
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                                        <div className="space-y-2">
                                            <label className="text-[10px] font-black uppercase text-slate-500">Full Name *</label>
                                            <input 
                                                type="text" 
                                                name="name"
                                                value={formData.name}
                                                onChange={handleChange}
                                                required 
                                                className="w-full border-b-2 border-slate-100 py-4 outline-none focus:border-[#cc0000] font-bold text-sm bg-slate-50 px-4" 
                                                placeholder="Dr. Satyam Palit" 
                                            />
                                        </div>
                                        <div className="space-y-2">
                                            <label className="text-[10px] font-black uppercase text-slate-500">Email Address *</label>
                                            <input 
                                                type="email" 
                                                name="email"
                                                value={formData.email}
                                                onChange={handleChange}
                                                required 
                                                className="w-full border-b-2 border-slate-100 py-4 outline-none focus:border-[#cc0000] font-bold text-sm bg-slate-50 px-4" 
                                                placeholder="satyam@example.com" 
                                            />
                                        </div>
                                    </div>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                                        {/* Select Program FIRST */}
                                        <div className="space-y-2">
                                            <label className="text-[10px] font-black uppercase text-slate-500">Select Program *</label>
                                            <select 
                                                name="subject"
                                                value={formData.subject}
                                                onChange={handleProgramChange}
                                                required
                                                className="w-full border-b-2 border-slate-100 py-4 outline-none focus:border-[#cc0000] font-bold text-sm bg-slate-50 px-4"
                                            >
                                                {allPrograms.length > 0 ? (
                                                    allPrograms.map((p, idx) => (
                                                        <option key={idx} value={p}>{p}</option>
                                                    ))
                                                ) : (
                                                    <option value="">No Programs Available</option>
                                                )}
                                            </select>
                                        </div>
                                        {/* Select Institution filtered by chosen program */}
                                        <div className="space-y-2">
                                            <label className="text-[10px] font-black uppercase text-slate-500">Select Institution *</label>
                                            <select 
                                                name="college"
                                                value={formData.college}
                                                onChange={handleCollegeChange}
                                                required
                                                className="w-full border-b-2 border-slate-100 py-4 outline-none focus:border-[#cc0000] font-bold text-sm bg-slate-50 px-4"
                                            >
                                                {filteredColleges.length > 0 ? (
                                                    filteredColleges.map((c, idx) => (
                                                        <option key={idx} value={c.name}>{c.name}</option>
                                                    ))
                                                ) : (
                                                    <option value="">No Matching Institutions</option>
                                                )}
                                            </select>
                                        </div>
                                    </div>

                                    <div className="grid grid-cols-1 md:grid-cols-1 gap-8">
                                        <div className="space-y-2">
                                            <label className="text-[10px] font-black uppercase text-slate-500">Phone Number *</label>
                                            <input 
                                                type="tel" 
                                                name="phone"
                                                value={formData.phone}
                                                onChange={handleChange}
                                                required 
                                                className="w-full border-b-2 border-slate-100 py-4 outline-none focus:border-[#cc0000] font-bold text-sm bg-slate-50 px-4" 
                                                placeholder="+91 00000 00000" 
                                            />
                                        </div>
                                    </div>
                                    <div className="space-y-2">
                                        <label className="text-[10px] font-black uppercase text-slate-500">Your Message *</label>
                                        <textarea 
                                            name="message"
                                            value={formData.message}
                                            onChange={handleChange}
                                            required 
                                            rows="5" 
                                            className="w-full border-b-2 border-slate-100 py-4 outline-none focus:border-[#cc0000] font-bold text-sm bg-slate-50 px-4 resize-none" 
                                            placeholder="How can we help you?"
                                        ></textarea>
                                    </div>
                                    
                                    <button 
                                        type="submit" 
                                        disabled={formStatus === 'sending'}
                                        className="bg-slate-900 text-white px-12 py-5 font-black uppercase text-xs tracking-[5px] hover:bg-[#cc0000] transition-all flex items-center shadow-2xl disabled:opacity-50"
                                    >
                                        {formStatus === 'sending' ? 'Sending Request...' : (
                                            <>
                                                Send Message <Send size={16} className="ml-4" />
                                            </>
                                        )}
                                    </button>
                                    
                                    {formStatus === 'success' && (
                                        <div className="p-4 bg-green-50 text-green-700 font-bold text-sm border-l-4 border-green-500 rounded-sm">
                                            Thank you! Your message has been sent successfully. We will get back to you soon.
                                        </div>
                                    )}

                                    {formStatus === 'error' && (
                                        <div className="p-4 bg-red-50 text-red-700 font-bold text-sm border-l-4 border-red-500 rounded-sm">
                                            Something went wrong. Please try again later.
                                        </div>
                                    )}
                                </form>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Google Maps Live Preview */}
            <section className="pb-24 px-4 bg-white">
                <div className="max-w-7xl mx-auto">
                    <div className="text-center mb-12">
                        <p className="text-[10px] font-black uppercase tracking-[5px] text-[#cc0000] mb-2 leading-none italic">Live Campus View</p>
                        <h4 className="text-2xl font-black uppercase text-slate-900 tracking-tighter">Find us on <span className="text-[#cc0000]">Maps</span></h4>
                    </div>
                    <div className="w-full h-[500px] shadow-2xl rounded-sm overflow-hidden border border-slate-100 relative group">
                        <iframe 
                            src={content.contact_map || "https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3682.2624592415045!2d88.3224!3d22.644001000000003!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x39f882cd1de08c8f:0xc39cbe2a80363b59!2sSurendralal%20Das%20Teachers'%20Training%20College%20%7C%20Best%20B.ed%20College%7C%20Best%20M.ed%20College%20Howrah%20%7C%20Kolkata!5e0!3m2!1sen!2sin!4v1774241287501!5m2!1sen!2sin"} 
                            width="100%" 
                            height="100%" 
                            style={{ border: 0 }} 
                            allowFullScreen="" 
                            loading="lazy" 
                            referrerPolicy="no-referrer-when-downgrade"
                            title="Arya Sabhyata Campus Location"
                        ></iframe>
                    </div>
                </div>
            </section>


            <Footer data={content} />

            <EnquiryModal isOpen={isInquiryOpen} onClose={() => setIsInquiryOpen(false)} />
        </div>
    );
};

export default Contact;
